import { css, html } from 'react-strict-dom';
import { Text } from 'src/components';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { Model } from 'src/models';

/**
 * How far past the deadline the strip will keep drawing.  Beyond this the
 * overrun stops growing, so a badly overdue order still shows its span.
 */
const MAX_OVERRUN = 1.4;

/**
 * There is no token for a red darker than the accent, and the overrun needs to
 * sit against it without reading as a separate state.
 */
const OVERRUN_COLOR = '#8f2424';

type Props = {
  track: Model.ServiceOrder.DateTrack;
};

/**
 * A service order's dates drawn as the calendar they are: weeks from the start
 * of the work to the stage in flight being due, with the deadline marked.
 * Deliberately unlike ProgressBar, which measures a budget being spent.
 */
export default function ServiceOrderDateTrack({ track }: Props) {
  const { elapsed, tone, startLabel, dueLabel, countdown } = track;

  // Overdue, the strip runs to today and the deadline sits back along it;
  // otherwise the deadline is the end of the strip.
  const drawn = Math.min(elapsed, MAX_OVERRUN);
  const deadlineAt = elapsed > 1 ? (1 / drawn) * 100 : 100;
  const filledTo = elapsed > 1 ? 100 : elapsed * 100;

  return (
    <html.div style={[Style.row, Style.alignCenter, styles.wrap]}>
      <Text typeface="caption.medium" color="neutral60">
        {startLabel}
      </Text>

      <html.span style={styles.track}>
        <html.span
          style={[
            styles.filled,
            styles.width(filledTo),
            'warning' === tone && styles.warning,
            'danger' === tone && styles.danger,
          ]}
        />
        {elapsed > 1 && (
          <html.span style={[styles.overrun, styles.from(deadlineAt)]} />
        )}
        <html.span style={styles.weeks} />
        <html.span style={[styles.deadline, styles.deadlineAt(deadlineAt)]} />
      </html.span>

      <Text typeface="caption.medium" color="neutral60" style={styles.full}>
        {countdown === null ? dueLabel : `${dueLabel} · ${countdown}`}
      </Text>
      {/* Only one of the two fits once the card narrows, so the countdown
          takes the space where there is one. */}
      <Text typeface="caption.medium" color="neutral60" style={styles.short}>
        {countdown ?? dueLabel}
      </Text>
    </html.div>
  );
}

const TRACK_HEIGHT = 10;

const styles = css.create({
  wrap: {
    gap: 8,
  },
  track: {
    position: 'relative',
    flexShrink: 0,
    height: TRACK_HEIGHT,
    borderRadius: 2,
    backgroundColor: color.neutral15,
    overflow: 'hidden',
    width: {
      default: 140,
      '@media (max-width: 640px)': 100,
    },
  },
  filled: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: color.neutral40,
  },
  warning: {
    backgroundColor: color.yellow,
  },
  danger: {
    backgroundColor: color.accent,
  },
  overrun: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: OVERRUN_COLOR,
  },
  // The week rules are what make this read as a calendar rather than a bar.
  weeks: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundImage:
      'repeating-linear-gradient(to right, transparent 0 13px, #ffffff 13px 14px)',
  },
  deadline: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: color.neutral80,
  },
  width: (percent: number) => ({
    width: `${percent}%`,
  }),
  from: (percent: number) => ({
    left: `${percent}%`,
  }),
  deadlineAt: (percent: number) => ({
    left: percent >= 100 ? 'calc(100% - 2px)' : `${percent}%`,
  }),
  full: {
    display: {
      default: 'block',
      '@media (max-width: 640px)': 'none',
    },
  },
  short: {
    display: {
      default: 'none',
      '@media (max-width: 640px)': 'block',
    },
  },
});
